<template>
	<div>
		<div class="relative">
			<ul ref="carousel" class="carousel-container flex overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 space-x-8 tablet:space-x-16">
				<li v-for="(item, index) in list" :key="index" class="carousel-slide flex-shrink-0 w-full tablet:w-[calc(50%-8px)] tabletWide:w-[calc(33.33%-10.66px)]">
					<slot name="itemContent" :item="item" />
				</li>
			</ul>
			<div v-if="isDesktop && scrollable">
				<transition name="fade">
					<button v-show="showLeftButton" class="button button-goPinkGhost w-[36px] h-[36px] absolute top-1/2 -translate-y-1/2 z-[1] left-[-45px]" aria-label="Scroll left" @click="handleButtonClick('prev')">
						<svg-icon name="arrow-back" class="iconSize-24" />
					</button>
				</transition>
				<transition name="fade">
					<button v-show="showRightButton" class="button button-goPinkGhost w-[36px] h-[36px] absolute top-1/2 -translate-y-1/2 z-[1] right-[-45px]" aria-label="Scroll right" @click="handleButtonClick('next')">
						<svg-icon name="arrow-forward" class="iconSize-24" />
					</button>
				</transition>
			</div>
		</div>
		<div v-if="scrollable" class="flex space-x-0.5 justify-center pt-16">
			<button v-for="page in totalPages" :key="page" class="p-6 rounded-full hover:bg-goPinkBrighter transition-colors duration-150 ease-out" :aria-label="`Show page ${page}`" @click="scrollToPage(page)">
				<div :class="['w-2 h-2 rounded-full transition-all duration-150 ease-in-out bg-goPink', page === currentPage ? 'opacity-100 scale-125' : 'opacity-50']" />
			</button>
		</div>
	</div>
</template>

<script>
import debounce from "lodash/debounce"

export default {
	props: {
		list: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			scrollable: false,
			showLeftButton: false,
			showRightButton: true,
			currentPage: 1,
			totalPages: 0,
		}
	},
	computed: {
		isDesktop() {
			return this.$store.state.browser.breakpoint.isDesktop
		}
	},
	mounted() {
		this.setTotalPages()
		this.debouncedCarouselScroll = debounce(this.handleCarouselScroll, 50)
		this.$refs.carousel.addEventListener("scroll", this.debouncedCarouselScroll)
		this.resizeObserver = new ResizeObserver(this.handleWindowResize)
  		this.resizeObserver.observe(this.$refs.carousel)
	},
	beforeDestroy() {
		this.$refs.carousel.removeEventListener("scroll", this.debouncedCarouselScroll)
		this.resizeObserver.disconnect()
	},
	methods: {
		handleCarouselScroll() {
			this.checkPagination()
		},
		handleWindowResize() {
			this.setTotalPages()
			this.checkPagination()
		},
		setTotalPages() {
			const carousel = this.$refs.carousel
			const slide = carousel.querySelector(".carousel-slide")
			const totalSlides = this.list.length
			const visibleSlides = Math.floor(carousel.clientWidth / slide.offsetWidth)
			this.totalPages = Math.max(1, totalSlides - visibleSlides + 1)
			this.scrollable = carousel.scrollWidth > carousel.clientWidth + 1
		},
		checkPagination() {
			const { carousel } = this.$refs
			const scrollPercent = carousel.scrollLeft / (carousel.scrollWidth - carousel.clientWidth)
			this.currentPage = Math.round(scrollPercent * (this.totalPages - 1)) + 1
			if (this.isDesktop) {
				const scrollTolerance = 2
				this.showLeftButton = carousel.scrollLeft > 0
				this.showRightButton = carousel.scrollLeft + carousel.clientWidth < carousel.scrollWidth - scrollTolerance
			}
		},
		scrollToPage(page) {
			const carousel = this.$refs.carousel
			const maxScroll = carousel.scrollWidth - carousel.clientWidth
			const scrollX = maxScroll * ((page - 1) / (this.totalPages - 1))
			carousel.scrollTo({ left: scrollX, behavior: "smooth" })
			this.currentPage = page
		},
		handleButtonClick(direction) {
			const carousel = this.$refs.carousel
			const slide = carousel.querySelector(".carousel-slide")
			const newScroll = direction === "prev"
				? carousel.scrollLeft - slide.offsetWidth
				: carousel.scrollLeft + slide.offsetWidth
			const alignedScroll = Math.round(newScroll / slide.offsetWidth) * slide.offsetWidth
			carousel.scrollTo({ left: alignedScroll, behavior: "smooth" })
		},
	},
}
</script>

<style scoped lang="scss">
.carousel-container {
	scrollbar-width: none;
	&::-webkit-scrollbar {
		display: none;
	}
	@media screen and (min-width: theme('screens.desktop')) {
		scroll-snap-type: x mandatory;
	}
}
.carousel-slide {
	@media screen and (min-width: theme('screens.desktop')) {
		scroll-snap-align: start;
	}
}
.fade-enter-active,
.fade-leave-active {
	transition: opacity 150ms ease;
}
.fade-enter,
.fade-leave-to {
	opacity: 0;
}
</style>
